import { Organization } from '../models/Company';
import { apiClient } from './ApiService';

/**
 * Update organization details
 * @param orgId - The organization ID
 * @param data - The organization data to update
 * @returns Promise with the updated organization
 */
const updateOrganizationDetails = async (
  orgId: number,
  data: { name: string; logo?: File }
): Promise<Organization> => {
  try {
    // If there's a logo file, we need to use FormData
    if (data.logo && data.logo instanceof File) {
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('logo', data.logo);

      // Use fetch directly for FormData
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/account/organizations/${orgId}/`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Error updating organization: ${response.statusText}`);
      }

      return await response.json();
    }

    // If no logo file, use the regular API client
    return await apiClient.patch<Organization>(`/account/organizations/${orgId}/`, { name: data.name });
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error updating organization details:', error);
    }
    throw error;
  }
};

/**
 * Get organization details
 * @param orgId - The organization ID
 * @returns Promise with the organization details
 */
const getOrganizationDetails = async (orgId: number): Promise<Organization> => {
  try {
    return await apiClient.get<Organization>(`/account/organizations/${orgId}/`, {}, true);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error(`Error fetching details for organization ${orgId}:`, error);
    }
    throw error;
  }
};

export const OrganizationService = {
  updateOrganizationDetails,
  getOrganizationDetails
};
